import React, { useState, useEffect } from 'react';
import {
  Window,
  WindowHeader,
  WindowContent,
  Button,
  TextInput,
  Fieldset,
  Toolbar,
  Separator,
  GroupBox,
  Select,
  Checkbox
} from 'react95';
import { usePaymentArrangements } from '../hooks';
import { 
  PAYMENT_FREQUENCIES,
  createPaymentArrangement,
  validatePaymentArrangement,
  getNextDueDate,
  calculateArrears,
  getDaysUntilDue,
  isArrangementOverdue
} from '../models/PaymentArrangement';
import { calculatePaymentSchedule } from '../utils/financialCalculations';
import { ERROR_MESSAGES, SUCCESS_MESSAGES } from '../models/constants';

/**
 * Payment Arrangement Form Component
 * 
 * Features implemented according to task 11 requirements:
 * - Create payment arrangement form component
 * - Add frequency and amount scheduling
 * - Implement due date calculations and tracking
 * - Add settlement option management
 * 
 * Requirements: 3.1, 3.2, 3.4, 3.5
 */
const PaymentArrangementForm = ({ 
  clientId = null,
  initialArrangement = null,
  onArrangementSaved = () => {},
  onClose = () => {}
}) => {
  const paymentArrangements = usePaymentArrangements();
  
  // Form state
  const [formData, setFormData] = useState(() => {
    if (initialArrangement) {
      // Ensure dates are Date objects, not strings
      return { 
        ...initialArrangement,
        nextDueDate: initialArrangement.nextDueDate instanceof Date 
          ? initialArrangement.nextDueDate 
          : new Date(initialArrangement.nextDueDate),
        createdAt: initialArrangement.createdAt instanceof Date
          ? initialArrangement.createdAt
          : new Date(initialArrangement.createdAt),
        updatedAt: initialArrangement.updatedAt instanceof Date
          ? initialArrangement.updatedAt
          : new Date(initialArrangement.updatedAt)
      };
    }
    return createPaymentArrangement({ clientId });
  });
  
  // UI state
  const [errors, setErrors] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [paymentSchedule, setPaymentSchedule] = useState([]);
  const [scheduleMonths, setScheduleMonths] = useState(12);

  // Calculate days until due and overdue status
  const [daysUntilDue, setDaysUntilDue] = useState(0);
  const [isOverdue, setIsOverdue] = useState(false);

  // Update due date calculations when nextDueDate changes
  useEffect(() => {
    if (formData.nextDueDate) {
      const days = getDaysUntilDue(formData);
      const overdue = isArrangementOverdue(formData);
      setDaysUntilDue(days);
      setIsOverdue(overdue);
    }
  }, [formData.nextDueDate, formData.isActive]);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    // Safely extract the value - React95 Select might pass an object
    let actualValue = value;
    if (value && typeof value === 'object' && value.value !== undefined) {
      actualValue = value.value;
    }
    
    // Handle numeric fields
    if (['amount', 'settleAmount', 'arrears'].includes(field)) {
      actualValue = actualValue === '' ? 0 : parseFloat(actualValue) || 0;
    }
    
    // Handle date fields
    if (field === 'nextDueDate' && actualValue) {
      actualValue = new Date(actualValue);
    }
    
    // Handle boolean fields
    if (field === 'settleForLess' || field === 'isActive') {
      actualValue = Boolean(actualValue);
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: actualValue,
      updatedAt: new Date()
    }));
    
    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
    }
    
    // Clear success message
    if (successMessage) {
      setSuccessMessage('');
    }
  };

  // Handle frequency change and auto-calculate next due date
  const handleFrequencyChange = (frequency) => {
    const actualFrequency = frequency && typeof frequency === 'object' ? frequency.value : frequency;
    
    // Calculate new due date based on frequency
    const newDueDate = getNextDueDate(actualFrequency);
    
    setFormData(prev => ({
      ...prev,
      frequency: actualFrequency,
      nextDueDate: newDueDate,
      updatedAt: new Date()
    }));
    
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  // Validate form data
  const validateForm = () => {
    const validation = validatePaymentArrangement(formData);
    setErrors(validation.errors);
    return validation.isValid;
  };

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      let result;
      
      if (formData.id && paymentArrangements.getById(formData.id)) {
        // Update existing arrangement
        result = await paymentArrangements.update(formData.id, formData);
      } else {
        // Create new arrangement
        result = await paymentArrangements.create(formData);
        if (result.success) {
          setFormData(result.data);
        }
      }
      
      if (result.success) {
        setSuccessMessage(SUCCESS_MESSAGES.ARRANGEMENT_SAVED || 'Payment arrangement saved successfully');
        onArrangementSaved(result.data);
      } else {
        setErrors(result.errors || [ERROR_MESSAGES.SAVE_ERROR]);
      }
    } catch (error) {
      console.error('Error saving payment arrangement:', error);
      setErrors([ERROR_MESSAGES.SAVE_ERROR]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle clear
  const handleClear = () => {
    const newArrangement = createPaymentArrangement({ clientId });
    setFormData(newArrangement);
    setErrors([]);
    setSuccessMessage('');
    setShowSchedule(false);
    setPaymentSchedule([]);
  };

  // Generate payment schedule
  const handleGenerateSchedule = () => {
    try {
      const schedule = calculatePaymentSchedule(formData, scheduleMonths);
      setPaymentSchedule(schedule);
      setShowSchedule(true);
    } catch (error) {
      console.error('Error generating payment schedule:', error);
      setErrors(['Failed to generate payment schedule']);
    }
  };

  // Format currency for display
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Format date for input
  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  return (
    <Window style={{ width: '700px', height: '550px', maxHeight: '90vh' }}>
      <WindowHeader>
        <span>📅 Payment Arrangement - {formData.id ? 'Edit' : 'New'}</span>
      </WindowHeader>
      
      {/* Toolbar */}
      <Toolbar style={{ justifyContent: 'flex-start' }}>
        <Button size="sm" onClick={handleSave} disabled={isLoading}>
          💾 Save
        </Button>
        <Button size="sm" onClick={handleClear}>
          🗑️ Clear
        </Button>
        <Separator orientation="vertical" />
        <Button size="sm" onClick={handleGenerateSchedule}>
          📊 Schedule
        </Button>
        <Separator orientation="vertical" />
        <Button size="sm" onClick={onClose}>
          ❌ Close
        </Button>
      </Toolbar>

      <WindowContent style={{ padding: '10px', maxHeight: 'calc(550px - 80px)', overflowY: 'auto' }}>
        
        {/* Error Display */}
        {errors.length > 0 && (
          <div style={{ 
            marginBottom: '10px', 
            padding: '10px', 
            backgroundColor: '#ffe0e0', 
            border: '1px solid #ff0000',
            color: '#cc0000'
          }}>
            <strong>⚠️ Please correct the following errors:</strong>
            <ul style={{ margin: '5px 0 0 20px' }}>
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div style={{ 
            marginBottom: '10px', 
            padding: '10px', 
            backgroundColor: '#e0ffe0', 
            border: '1px solid #00aa00',
            color: '#006600'
          }}>
            ✅ {successMessage}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px',maxHeight: '490px' }}>
          
          {/* Main Form Area */}
          <div style={{ flex: 1 }}>
            <GroupBox label="Arrangement Details">
              <div style={{ padding: '0px', margintop:'0px' }}>
                
                {/* Payment Amount and Frequency Row */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
                      Payment Amount: *
                    </label>
                    <TextInput
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.amount}
                      onChange={(e) => handleInputChange('amount', e.target.value)}
                      placeholder="0.00"
                      style={{ width: '100%' }}
                      disabled={isLoading}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
                      Frequency: *
                    </label>
                    <Select
                      value={formData.frequency}
                      onChange={handleFrequencyChange}
                      options={[
                        { value: PAYMENT_FREQUENCIES.WEEKLY, label: 'Weekly' },
                        { value: PAYMENT_FREQUENCIES.BI_WEEKLY, label: 'Bi-Weekly' },
                        { value: PAYMENT_FREQUENCIES.MONTHLY, label: 'Monthly' },
                        { value: PAYMENT_FREQUENCIES.QUARTERLY, label: 'Quarterly' },
                        { value: PAYMENT_FREQUENCIES.ANNUALLY, label: 'Annually' }
                      ]}
                      style={{ width: '100%' }}
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* Next Due Date */}
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
                    Next Due Date: *
                  </label>
                  <TextInput
                    type="date"
                    value={formatDateForInput(formData.nextDueDate)}
                    onChange={(e) => handleInputChange('nextDueDate', e.target.value)}
                    style={{ width: '100%' }}
                    disabled={isLoading}
                  />
                  {formData.nextDueDate && (
                    <div style={{ fontSize: '10px', marginTop: '5px', color: isOverdue ? '#cc0000' : '#666' }}>
                      {isOverdue ? (
                        <span style={{ fontWeight: 'bold' }}>⚠️ OVERDUE by {Math.abs(daysUntilDue)} days</span>
                      ) : (
                        <span>Due in {daysUntilDue} days</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Arrears */}
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
                    Arrears:
                  </label>
                  <TextInput
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.arrears}
                    onChange={(e) => handleInputChange('arrears', e.target.value)}
                    placeholder="0.00"
                    style={{ width: '100%' }}
                    disabled={isLoading}
                  />
                  {formData.arrears > 0 && (
                    <div style={{ fontSize: '10px', marginTop: '5px', color: '#cc0000', fontWeight: 'bold' }}>
                      ⚠️ Outstanding arrears: {formatCurrency(formData.arrears)}
                    </div>
                  )}
                </div>

                {/* Active Status */}
                <div style={{ marginBottom: '10px' }}>
                  <Checkbox
                    checked={formData.isActive}
                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    label="Active Arrangement"
                    disabled={isLoading}
                  />
                  <div style={{ fontSize: '10px', color: '#666', marginLeft: '20px' }}>
                    {formData.isActive ? 'This arrangement is currently active' : 'This arrangement is inactive'}
                  </div>
                </div>
              </div>
            </GroupBox>

            {/* Settlement Options */}
            <GroupBox label="Settlement Options" style={{ marginTop: '10px' }}>
              <div style={{ padding: '10px' }}>
                <div style={{ marginBottom: '10px' }}>
                  <Checkbox
                    checked={formData.settleForLess}
                    onChange={(e) => handleInputChange('settleForLess', e.target.checked)}
                    label="Settle for Less Option Available"
                    disabled={isLoading}
                  />
                </div>

                {formData.settleForLess && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
                      Settlement Amount:
                    </label>
                    <TextInput
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.settleAmount}
                      onChange={(e) => handleInputChange('settleAmount', e.target.value)}
                      placeholder="0.00"
                      style={{ width: '100%' }}
                      disabled={isLoading}
                    />
                    {formData.settleAmount > 0 && (
                      <div style={{ fontSize: '10px', marginTop: '5px', color: '#cc6600' }}>
                        Settlement offer: {formatCurrency(formData.settleAmount)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </GroupBox>
          </div>

          {/* Summary Sidebar */}
          <div style={{ width: '250px' }}>
            <GroupBox label="Arrangement Summary">
              <div style={{ padding: '10px', fontSize: '11px' }}>
                <div style={{ marginBottom: '10px' }}>
                  <strong>Payment Amount:</strong><br/>
                  <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#006600' }}>
                    {formatCurrency(formData.amount)}
                  </span>
                </div>
                
                <div style={{ marginBottom: '10px' }}>
                  <strong>Frequency:</strong><br/>
                  <span style={{ textTransform: 'capitalize' }}>
                    {formData.frequency}
                  </span>
                </div>
                
                <div style={{ marginBottom: '10px' }}>
                  <strong>Next Payment:</strong><br/>
                  <span style={{ fontSize: '12px' }}>
                    {formData.nextDueDate ? new Date(formData.nextDueDate).toLocaleDateString() : 'Not set'}
                  </span>
                </div>
                
                {formData.arrears > 0 && (
                  <div style={{ marginBottom: '10px', paddingTop: '10px', borderTop: '1px solid #c0c0c0' }}>
                    <strong style={{ color: '#cc0000' }}>Arrears:</strong><br/>
                    <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#cc0000' }}>
                      {formatCurrency(formData.arrears)}
                    </span>
                  </div>
                )}
                
                {formData.settleForLess && formData.settleAmount > 0 && (
                  <div style={{ marginBottom: '10px', paddingTop: '10px', borderTop: '1px solid #c0c0c0' }}>
                    <strong style={{ color: '#cc6600' }}>Settlement:</strong><br/>
                    <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#cc6600' }}>
                      {formatCurrency(formData.settleAmount)}
                    </span>
                  </div>
                )}
                
                <div style={{ paddingTop: '10px', borderTop: '1px solid #c0c0c0' }}>
                  <strong>Status:</strong><br/>
                  <span style={{ 
                    padding: '2px 6px',
                    fontSize: '10px',
                    backgroundColor: formData.isActive ? '#006600' : '#666',
                    color: 'white',
                    borderRadius: '3px'
                  }}>
                    {formData.isActive ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                </div>
              </div>
            </GroupBox>

            {/* Schedule Generator */}
            <GroupBox label="Payment Schedule" style={{ marginTop: '10px' }}>
              <div style={{ padding: '10px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
                  Number of Payments:
                </label>
                <TextInput
                  type="number"
                  min="1"
                  max="60"
                  value={scheduleMonths}
                  onChange={(e) => setScheduleMonths(parseInt(e.target.value) || 12)}
                  style={{ width: '100%', marginBottom: '10px' }}
                />
                <Button 
                  size="sm" 
                  onClick={handleGenerateSchedule}
                  style={{ width: '100%' }}
                >
                  Generate Schedule
                </Button>
              </div>
            </GroupBox>
          </div>
        </div>

        {/* Payment Schedule Display */}
        {showSchedule && paymentSchedule.length > 0 && (
          <Fieldset label="Payment Schedule" style={{ marginTop: '10px' }}>
            <div style={{ padding: '10px', maxHeight: '160px', overflow: 'auto' }}>
              <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#c0c0c0' }}>
                    <th style={{ padding: '5px', textAlign: 'left', border: '1px solid #808080' }}>Payment #</th>
                    <th style={{ padding: '5px', textAlign: 'left', border: '1px solid #808080' }}>Due Date</th>
                    <th style={{ padding: '5px', textAlign: 'right', border: '1px solid #808080' }}>Amount</th>
                    <th style={{ padding: '5px', textAlign: 'right', border: '1px solid #808080' }}>Cumulative</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentSchedule.map((payment, index) => (
                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#f8f8f8' }}>
                      <td style={{ padding: '5px', border: '1px solid #c0c0c0' }}>{payment.paymentNumber}</td>
                      <td style={{ padding: '5px', border: '1px solid #c0c0c0' }}>
                        {new Date(payment.dueDate).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '5px', textAlign: 'right', border: '1px solid #c0c0c0' }}>
                        {formatCurrency(payment.amount)}
                      </td>
                      <td style={{ padding: '5px', textAlign: 'right', border: '1px solid #c0c0c0', fontWeight: 'bold' }}>
                        {formatCurrency(payment.cumulativeAmount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Fieldset>
        )}

        {/* Status Bar */}
        <div style={{ 
          marginTop: '10px', 
          padding: '5px 10px', 
          backgroundColor: '#c0c0c0', 
          border: '1px inset #c0c0c0',
          fontSize: '11px',
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <span>
            {formData.id ? `Arrangement ID: ${formData.id}` : 'New Arrangement'}
          </span>
          <span>
            {formData.clientId && `Client ID: ${formData.clientId}`}
          </span>
          <span>
            {formData.createdAt && `Created: ${new Date(formData.createdAt).toLocaleDateString()}`}
          </span>
        </div>
      </WindowContent>
    </Window>
  );
};

export default PaymentArrangementForm;
